import { JsonEditorSchema } from "@/shared/JsonEditorSchema/models/JsonEditorSchema";
import { JsonEditorType } from "@/shared/JsonEditorSchema/models/JsonEditorType";
import IJsonEditorSchemaGenerator, { GeneratorOptions } from "./IJsonEditorSchemaGenerator";
import { ISchema } from "@/shared/models/ISchema";


export abstract class SchemaGenerator<TRootSchema> implements IJsonEditorSchemaGenerator<TRootSchema> {

    public abstract createRootSchema(): JsonEditorSchema<TRootSchema>;

    public abstract mapType(fieldType: any): JsonEditorType | null;

    public abstract populateVariants(schema: JsonEditorSchema<TRootSchema>, variants: Array<any>, options: any,): void;

    public generate(cdnSchema: Array<ISchema>, options?: GeneratorOptions): JsonEditorSchema<TRootSchema> {

        const opts = {
            additionalProperties: options?.additionalProperties ?? true,
            requireFields: options?.requireFields ?? true
        };

        const schema = this.createRootSchema();

        const schemas: Array<any> = Array.isArray(cdnSchema) ? cdnSchema : [];

        const buildPropertyForField = (field: any): any => {
            const prop: any = {};

            const resolvedType = this.mapType(field.field_type);
            if (resolvedType != null) prop.type = resolvedType;

            if (field.options && Array.isArray(field.options) && field.options.length > 0) {
                prop.enum = field.options.map((o: any) => o.value);
            }

            if (field.default_value !== undefined) prop.default = field.default_value;

            //TODO Special case for regex? Is pattern a respected value in our schema?
            //TODO what about an internal hex code regex for colorpicker?
            if (field.field_type === 'regex') {
                const pattern = field.field_type_args?.regex_pattern ?? field.field_type_args?.pattern;
                if (pattern) prop.pattern = pattern;
            }

            // Arrays
            if ((field.field_type === 'array') && field.item_schema) {
                const itemSchema = buildPropertyForField(field.item_schema as any);
                prop.items = itemSchema;
            }

            // Objects
            if ((field.field_type === 'object') && Array.isArray(field.fields)) {
                const child: any = {
                    type: 'object',
                    properties: {},
                    additionalProperties: opts.additionalProperties,
                    required: []
                };
                for (const f of field.fields as Array<any>) {
                    child.properties[f.name] = buildPropertyForField(f);
                    if (opts.requireFields && f.required === true) child.required.push(f.name);
                }
                child.required = [...new Set(child.required)];
                prop.type = 'object';
                prop.properties = child.properties;
                if (child.required && child.required.length > 0) prop.required = child.required;
                prop.additionalProperties = child.additionalProperties;
            }

            return prop;
        };

        const variants: Array<any> = [];
        for (const rs of schemas) {
            if (rs == null || typeof rs.type !== 'string') continue;

            const variant: any = {
                type: 'object',
                additionalProperties: opts.additionalProperties,
                properties: {
                    type: { type: 'string', enum: [rs.type] }
                },
                required: ['type']
            };

            if (Array.isArray(rs.fields) && rs.fields.length > 0) {
                for (const f of rs.fields as Array<any>) {
                    variant.properties[f.name] = buildPropertyForField(f);
                    if (opts.requireFields && f.required === true) {
                        variant.required = variant.required || [];
                        variant.required.push(f.name);
                    }
                }
            }

            if (rs.payload_template && typeof rs.payload_template === 'object') {
                for (const k of Object.keys(rs.payload_template)) {
                    if (!variant.properties[k]) variant.properties[k] = {};
                }
            }

            variants.push(variant);
        }

        if (variants.length > 0) {
            this.populateVariants(schema, variants, opts);
        }

        if (schema.required && schema.required.length === 0) delete schema.required;

        return schema;
    }
}

export default SchemaGenerator;
